import { api } from "@Aspecto/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NavigationGuard } from "@/components/ui/navigation-guard";
import { SectionCard } from "@/components/ui/section-card";
import { Switch } from "@/components/ui/switch";
import { TimezoneSelect } from "@/components/ui/timezone-select";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/settings/")({
  component: ProfileSettings,
});

function ProfileSettings() {
  const { data: user } = useCurrentUser();
  const updateProfile = useMutation(api.users.updateProfile);
  const updatePreferences = useMutation(api.users.updateNotificationPreferences);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  /*
  const hasPassword = clerkUser?.passwordEnabled ?? false;
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const updatePassword = useReverification(
    async (params: {
      newPassword: string;
      currentPassword?: string;
      signOutOfOtherSessions: boolean;
    }) => {
      return clerkUser?.updatePassword(params);
    },
  );
  */

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setTimezone(user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }, [user]);

  const isDirty =
    !!user &&
    (firstName !== (user.firstName || "") ||
      lastName !== (user.lastName || "") ||
      timezone !== (user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and last name are required");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        timezone: timezone || undefined,
      });
      toast.success("Profile saved successfully!");
    } catch {
      toast.error("Failed to save profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDigestToggle = async (enabled: boolean) => {
    try {
      await updatePreferences({
        notifyWeeklyDigest: enabled,
        notifyEventReminders: user?.notifyEventReminders ?? true,
        notifyCommunityInvitations: user?.notifyCommunityInvitations ?? true,
      });
      toast.success(`Digest ${enabled ? "enabled" : "disabled"} successfully!`);
    } catch {
      toast.error("Failed to update Digest preference.");
    }
  };

  /*
  const handlePasswordSubmit = async () => {
    setPasswordError("");

    if (!newPassword) {
      setPasswordError("New password is required.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    if (hasPassword && !currentPassword) {
      setPasswordError("Current password is required.");
      return;
    }

    setPasswordSubmitting(true);
    try {
      await updatePassword({
        newPassword,
        currentPassword: hasPassword ? currentPassword : undefined,
        signOutOfOtherSessions: false,
      });
      toast.success(hasPassword ? "Password updated successfully!" : "Password set successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangingPassword(false);
      setPasswordError("");
    } catch (err) {
      if (isReverificationCancelledError(err)) {
        return;
      }
      if (isClerkAPIResponseError(err)) {
        const clerkError = err.errors[0];
        switch (clerkError.code) {
          case "form_password_incorrect":
            setPasswordError("Current password is incorrect.");
            break;
          case "form_password_pwned":
            setPasswordError(
              "This password has been found in a data breach. Please choose a different one.",
            );
            break;
          case "form_password_length_too_short":
            setPasswordError("Password is too short.");
            break;
          case "form_password_not_strong_enough":
            setPasswordError("Password is not strong enough.");
            break;
          case "form_new_password_matches_current":
            setPasswordError("New password must be different from your current password.");
            break;
          default:
            setPasswordError(clerkError.longMessage || clerkError.message);
        }
      } else {
        setPasswordError("Failed to update password. Please try again.");
      }
    } finally {
      setPasswordSubmitting(false);
    }
  };
  */

  return (
    <form onSubmit={handleSave}>
      <NavigationGuard isDirty={isDirty} />
      <SectionCard title="Account" description="Your personal information">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user?.email || ""} disabled />
            <p className="text-xs text-muted-foreground">
              Email is managed by your authentication provider.
            </p>
          </div>
          {/*
          <div className="space-y-2">
            <Label>Password</Label>
            {!changingPassword ? (
              <div className="flex items-center justify-between rounded-lg border border-input px-3 py-2">
                <span className="text-sm text-muted-foreground">
                  {hasPassword ? "••••••••" : "No password set"}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setChangingPassword(true)}
                >
                  {hasPassword ? "Change" : "Set password"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3 rounded-lg border border-input p-3">
                {hasPassword && (
                  <div className="space-y-1.5">
                    <Label htmlFor="currentPassword" className="text-xs">
                      Current password
                    </Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={passwordSubmitting}
                      autoComplete="current-password"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword" className="text-xs">
                    New password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={passwordSubmitting}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-xs">
                    Confirm new password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={passwordSubmitting}
                    autoComplete="new-password"
                  />
                </div>
                {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={passwordSubmitting}
                    onClick={() => {
                      setChangingPassword(false);
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setPasswordError("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={passwordSubmitting}
                    onClick={handlePasswordSubmit}
                  >
                    {passwordSubmitting ? "Saving..." : hasPassword ? "Update" : "Set password"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          */}
          <div className="space-y-2">
            <Label>Time zone</Label>
            <TimezoneSelect value={timezone} onChange={setTimezone} disabled={isSubmitting} />
          </div>
          <p className="text-xs text-muted-foreground">
            Your name and time zone are visible to other members of your organization.
          </p>
        </div>
      </SectionCard>
      <div id="weekly-digest" className="mt-6">
        <SectionCard title="Digest" description="Choose whether you receive digest emails">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="weekly-digest-enabled">Enable Digest</Label>
              <p className="text-xs text-muted-foreground">
                Receive digest emails when your organization sends them.
              </p>
            </div>
            <Switch
              id="weekly-digest-enabled"
              checked={user?.notifyWeeklyDigest ?? true}
              onCheckedChange={handleDigestToggle}
            />
          </div>
        </SectionCard>
      </div>
      <div className="mt-6 flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </form>
  );
}
